import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Loader2 } from 'lucide-react';

import { PartnerCatalogImageCell } from 'components/fbm/components/partnerCatalog/PartnerCatalogImageCell';
import {
    getPartnerIdColumnLabel,
    PartnerCatalogSortConfig,
} from 'components/fbm/components/partnerCatalog/partnerCatalogUtils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { cn } from 'shadcn/lib/utils';
import { PaginationControls, ResultsCounter } from 'shared';

const renderEmptyCell = (value) => {
    if (value == null || value === '') {
        return null;
    }
    return value;
};

const renderBaseUnit = ({ row }) => {
    const size = row.original?.base_unit_size;
    const uom = row.original?.base_unit_uom;
    if (size == null && uom == null) {
        return null;
    }
    return [size, uom].filter((part) => part != null && part !== '').join(' ');
};

const renderNutritionCell = ({ row }) => (
    <PartnerCatalogImageCell
        src={row.original?.nutritional_info}
        alt={`Nutrition for ${row.original?.name ?? 'item'}`}
        label={`Nutrition for ${row.original?.name ?? 'item'}`}
    />
);

const renderProductPhotoCell = ({ row }) => (
    <PartnerCatalogImageCell
        src={row.original?.product_photo}
        alt={`Product photo for ${row.original?.name ?? 'item'}`}
        label={`Product photo for ${row.original?.name ?? 'item'}`}
    />
);

const renderSortIcon = (sorted) => {
    if (sorted === 'asc') {
        return <ArrowUp className="h-4 w-4 shrink-0" />;
    }
    if (sorted === 'desc') {
        return <ArrowDown className="h-4 w-4 shrink-0" />;
    }
    return <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />;
};

/**
 * @param {Object} props
 * @param {Array<Record<string, unknown>>} [props.data=[]]
 * @param {string} [props.fbmAccountName]
 * @param {boolean} [props.isLoading=false]
 * @param {boolean} [props.isFetching=false]
 * @param {string} [props.emptyMessage='No catalog items found.']
 * @param {number} [props.currentPage]
 * @param {number} [props.totalPages]
 * @param {number} [props.totalCount]
 * @param {number} [props.pageSize]
 * @param {number[]} [props.pageSizeOptions]
 * @param {boolean} [props.hasPrevious]
 * @param {boolean} [props.hasNext]
 * @param {() => void} [props.onPrevious]
 * @param {() => void} [props.onNext]
 * @param {(size: number) => void} [props.onPageSizeChange]
 * @param {Array<{id: string, desc: boolean}>} [props.sorting=[]]
 * @param {(columnId: string) => void} [props.onSortColumn]
 */
function PartnerCatalogTable({
    currentPage,
    data = [],
    emptyMessage = 'No catalog items found.',
    fbmAccountName,
    hasNext = false,
    hasPrevious = false,
    isFetching = false,
    isLoading = false,
    onNext,
    onPageSizeChange,
    onPrevious,
    onSortColumn,
    pageSize,
    pageSizeOptions,
    sorting,
    totalCount,
    totalPages,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const partnerIdLabel = getPartnerIdColumnLabel(fbmAccountName);
    const isSortable = (key) => Boolean(PartnerCatalogSortConfig.sortableFields[key]);
    const showInitialLoading = isLoading && tableData.length === 0;

    const columns = useMemo(
        () => [
            {
                accessorKey: 'partner_sku',
                header: partnerIdLabel,
                enableSorting: false,
                cell: ({ getValue }) => renderEmptyCell(getValue()),
                meta: { className: 'w-[8%]' },
            },
            {
                accessorKey: 'external_sku',
                header: 'MM External SKU',
                enableSorting: isSortable('external_sku'),
                cell: ({ getValue }) => renderEmptyCell(getValue()),
                meta: { className: 'w-[8%]' },
            },
            {
                accessorKey: 'name',
                header: 'Name',
                enableSorting: isSortable('name'),
                cell: ({ getValue }) => renderEmptyCell(getValue()),
                meta: { className: 'w-[14%]' },
            },
            {
                accessorKey: 'brand',
                header: 'Brand',
                enableSorting: isSortable('brand'),
                cell: ({ getValue }) => renderEmptyCell(getValue()),
                meta: { className: 'w-[8%]' },
            },
            {
                id: 'base_unit',
                accessorFn: (row) => row?.base_unit_size,
                header: 'Base Unit',
                enableSorting: isSortable('base_unit'),
                cell: renderBaseUnit,
                meta: { className: 'w-[7%]' },
            },
            {
                accessorKey: 'l1_taxonomy_name',
                header: 'L1',
                enableSorting: isSortable('l1_taxonomy_name'),
                cell: ({ getValue }) => renderEmptyCell(getValue()),
                meta: { className: 'w-[7%]' },
            },
            {
                accessorKey: 'l2_taxonomy_name',
                header: 'L2',
                enableSorting: isSortable('l2_taxonomy_name'),
                cell: ({ getValue }) => renderEmptyCell(getValue()),
                meta: { className: 'w-[7%]' },
            },
            {
                accessorKey: 'l3_taxonomy_name',
                header: 'L3',
                enableSorting: isSortable('l3_taxonomy_name'),
                cell: ({ getValue }) => renderEmptyCell(getValue()),
                meta: { className: 'w-[7%]' },
            },
            {
                accessorKey: 'ingredients',
                header: 'Ingredients',
                enableSorting: false,
                cell: ({ getValue }) => renderEmptyCell(getValue()),
                meta: { className: 'w-[10%]' },
            },
            {
                accessorKey: 'allergens',
                header: 'Allergens',
                enableSorting: false,
                cell: ({ getValue }) => renderEmptyCell(getValue()),
                meta: { className: 'w-[8%]' },
            },
            {
                accessorKey: 'nutritional_info',
                header: 'Nutrition',
                enableSorting: false,
                cell: renderNutritionCell,
                meta: { className: 'w-[6%]' },
            },
            {
                accessorKey: 'product_photo',
                header: 'Product Photo',
                enableSorting: false,
                cell: renderProductPhotoCell,
                meta: { className: 'w-[6%]' },
            },
        ],
        [partnerIdLabel],
    );

    const table = useReactTable({
        columns,
        data: tableData,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row, index) => (row?.external_sku != null ? String(row.external_sku) : String(index)),
        manualSorting: true,
        state: {
            sorting: sorting ?? [],
        },
    });

    const showPagination = Boolean(onPrevious && onNext && onPageSizeChange);

    return (
        <div className="flex flex-1 flex-col">
            {isFetching && !showInitialLoading ? (
                <div className="mb-2 h-1 w-full overflow-hidden rounded bg-muted">
                    <div className="h-full w-1/3 animate-pulse bg-primary/40" />
                </div>
            ) : null}
            {showPagination ? (
                <div className="mb-2">
                    <ResultsCounter
                        loadedCount={tableData.length}
                        totalCount={totalCount}
                        isRefreshing={isFetching && !showInitialLoading}
                    />
                </div>
            ) : null}
            <div className="flex min-h-[240px] flex-1 flex-col">
                {showInitialLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading catalog…</span>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-md border pb-2">
                        <Table className="w-full min-w-[1100px] [&_table]:table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            const canSort = header.column.getCanSort();
                                            const sorted = header.column.getIsSorted();
                                            return (
                                                <TableHead
                                                    key={header.id}
                                                    className={cn(
                                                        'whitespace-normal break-words text-left',
                                                        canSort ? 'cursor-pointer select-none' : null,
                                                        header.column.columnDef.meta?.className,
                                                    )}
                                                    onClick={
                                                        canSort ? () => onSortColumn?.(header.column.id) : undefined
                                                    }
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <div className="flex items-center gap-1">
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext(),
                                                            )}
                                                            {canSort ? renderSortIcon(sorted) : null}
                                                        </div>
                                                    )}
                                                </TableHead>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow key={row.id}>
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell
                                                    key={cell.id}
                                                    className={cn(
                                                        'whitespace-normal break-words text-left align-top',
                                                        cell.column.columnDef.meta?.className,
                                                    )}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {showPagination && tableData.length > 0 ? (
                            <PaginationControls
                                hasPrevious={hasPrevious}
                                hasNext={hasNext}
                                onPrevious={onPrevious}
                                onNext={onNext}
                                isLoading={isFetching}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                onPageSizeChange={onPageSizeChange}
                                pageSizeOptions={pageSizeOptions}
                            />
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
}

export { PartnerCatalogTable };
